import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { Trade, TradeFilters, TradeSummary } from '../../models/trade';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';
import { useSnackbar } from 'notistack';

// List trades with pagination and filtering (CRM version)
const fetchTrades = async (filters: TradeFilters = {}) => {
    const { data } = await httpClient.get<PaginatedResponse<Trade>>(API_ENDPOINTS.CRM.TRADES.LIST, {
        params: filters,
    });
    return data;
};

// Fetch trade summary/statistics (CRM version)
const fetchTradeSummary = async (filters: TradeFilters = {}) => {
    const { data } = await httpClient.get<ApiResponse<TradeSummary>>(
        API_ENDPOINTS.CRM.TRADES.SUMMARY,
        { params: filters }
    );
    return data.data;
};

export const useCrmTrades = (filters: TradeFilters = {}) => {
    return useQuery<PaginatedResponse<Trade>, Error>({
        queryKey: [API_ENDPOINTS.CRM.TRADES.LIST, filters],
        queryFn: () => fetchTrades(filters),
        refetchInterval: 10000,
    });
};

// Hook for trade summary/statistics (CRM version)
export const useCrmTradeSummary = (filters: TradeFilters = {}) => {
    return useQuery<TradeSummary, Error>({
        queryKey: [API_ENDPOINTS.CRM.TRADES.SUMMARY, filters],
        queryFn: () => fetchTradeSummary(filters),
    });
};

// Get trade by ID (CRM version)
const fetchTrade = async (id: string) => {
    const { data } = await httpClient.get<ApiResponse<Trade>>(API_ENDPOINTS.CRM.TRADES.DETAILS(id));
    return data.data;
};

export const useCrmTrade = (id: string) => {
    return useQuery<Trade, Error>({
        queryKey: [API_ENDPOINTS.CRM.TRADES.LIST, id],
        queryFn: () => fetchTrade(id),
        refetchInterval: 10000,
        enabled: !!id,
    });
};

// Update trade hook for inline editing (CRM version)
export const useCrmUpdateTrade = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({ tradeId, data }: { tradeId: string; data: any }) => {
            const { data: response } = await httpClient.put<ApiResponse>(
                API_ENDPOINTS.CRM.TRADES.DETAILS(tradeId),
                data
            );
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRADES.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRADES.SUMMARY] });
            enqueueSnackbar('Trade updated successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to update trade', {
                variant: 'error',
            });
        },
    });
};

// Delete trade hook (CRM version)
export const useCrmDeleteTrade = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (tradeId: string) => {
            const { data: response } = await httpClient.delete<ApiResponse>(
                API_ENDPOINTS.CRM.TRADES.DETAILS(tradeId)
            );
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRADES.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.TRADES.SUMMARY] });
            enqueueSnackbar('Trade deleted successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to delete trade', {
                variant: 'error',
            });
        },
    });
};

// Export trades hook (CRM version)
export const useCrmExportTrades = () => {
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async (filters: TradeFilters = {}) => {
            const response = await httpClient.get(API_ENDPOINTS.CRM.TRADES.EXPORT, {
                params: filters,
                responseType: 'blob',
            });
            return response;
        },
        onSuccess: (response, filters) => {
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `trades-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            enqueueSnackbar('Trades exported successfully', { variant: 'success' });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to export trades', {
                variant: 'error',
            });
        },
    });
};
